import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ease, duration } from "@/lib/motion";

type BrowserMockupProps = {
  ready?: boolean;
};

const BrowserMockup = ({ ready = true }: BrowserMockupProps) => {
  const prefersReducedMotion = useReducedMotion();
  const [notifyIn, setNotifyIn] = useState(false);

  useEffect(() => {
    if (!ready) return;
    const id = window.setTimeout(() => setNotifyIn(true), 1200);
    return () => window.clearTimeout(id);
  }, [ready]);

  return (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: duration.deliberate, ease: ease.smooth }}
    className="relative w-full max-w-xl"
    aria-hidden="true"
  >
    {/* Glow — inset-0 so layout width stays within parent; blur-2xl still renders softly past edges */}
    <div className="absolute inset-0 rounded-2xl bg-sage/10 blur-2xl pointer-events-none" />

    {/* Browser shell */}
    <div className="relative rounded-xl overflow-hidden border border-cream/10 shadow-2xl shadow-black/40">
      {/* Chrome bar */}
      <div className="bg-[hsl(220,5%,16%)] border-b border-cream/10 px-3 py-2.5 flex items-center gap-2.5">
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(5,70%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(38,80%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(130,60%,50%)]" />
        </div>
        {/* URL bar */}
        <div className="flex-1 mx-2 bg-[hsl(220,5%,12%)] rounded-md px-3 py-1 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-sage/60 shrink-0" />
          <span className="text-[10px] text-cream/50 font-mono truncate">ridgeriseroofing.com</span>
        </div>
        {/* Nav icons placeholder */}
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-4 rounded-sm bg-cream/10" />
          <div className="h-2.5 w-4 rounded-sm bg-cream/10" />
        </div>
      </div>

      {/* Website content — real Showcase after-image as proof of work */}
      <div className="relative bg-[hsl(220,8%,10%)] aspect-[16/10] overflow-hidden">
        <picture>
          <source
            type="image/avif"
            srcSet="/showcases/roofing-1-after-400w.avif 400w, /showcases/roofing-1-after-800w.avif 800w, /showcases/roofing-1-after-1200w.avif 1200w, /showcases/roofing-1-after-1600w.avif 1600w"
            sizes="(max-width: 1024px) 92vw, 560px"
          />
          <source
            type="image/webp"
            srcSet="/showcases/roofing-1-after-400w.webp 400w, /showcases/roofing-1-after-800w.webp 800w, /showcases/roofing-1-after-1200w.webp 1200w, /showcases/roofing-1-after-1600w.webp 1600w"
            sizes="(max-width: 1024px) 92vw, 560px"
          />
          <img
            src="/showcases/roofing-1-after-800w.jpg"
            srcSet="/showcases/roofing-1-after-400w.jpg 400w, /showcases/roofing-1-after-800w.jpg 800w, /showcases/roofing-1-after-1200w.jpg 1200w, /showcases/roofing-1-after-1600w.jpg 1600w"
            sizes="(max-width: 1024px) 92vw, 560px"
            alt=""
            width={800}
            height={500}
            fetchPriority="high"
            decoding="async"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
          />
        </picture>
      </div>
    </div>

    {/* Floating notification — outside bottom-right corner */}
    <motion.div
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={notifyIn ? (prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }) : undefined}
      transition={{ duration: duration.smooth, ease: ease.smooth }}
      className="absolute top-full right-2 md:right-4 -mt-3 bg-[hsl(220,5%,16%)] border border-cream/10 rounded-xl px-4 py-3 shadow-xl shadow-black/30 flex items-center gap-3 min-w-[180px]"
    >
      <div className="h-7 w-7 rounded-lg bg-sage/20 flex items-center justify-center shrink-0">
        {prefersReducedMotion ? (
          <div className="h-2.5 w-2.5 rounded-full bg-sage" />
        ) : (
          <motion.div
            className="h-2.5 w-2.5 rounded-full bg-sage"
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: ease.gentle }}
          />
        )}
      </div>
      <div>
        <p className="text-[10px] font-semibold text-cream leading-none mb-0.5">New lead received</p>
        <p className="text-[9px] text-cream/60">Contact form · just now</p>
      </div>
    </motion.div>
  </motion.div>
  );
};

export default BrowserMockup;
